import type { AiCapabilityPage } from "../schema.js";
import { LIVE, MANAGER_ROLES, SLOT_GROUP, WRITE_FLAGS } from "./_shared.js";

/**
 * `PAY-CONFIRM-DECIDE` (@UC0199) — thủ quỹ XÁC NHẬN hoặc TỪ CHỐI một lời báo đã chuyển, T2. Trigger `DUTY_CREATED` cho thủ quỹ.
 * GUI có HAI route (`approve` / `decline`) trên `ApproveDeclineService` (payments/approve-decline.service.ts:160,527). Trang này là
 * MỘT việc với ô `quyetDinh` ⇒ executor `approveDecline.decide`: 0023 bind nó thành "gọi `approve` hay `decline` theo ô" — cùng
 * hai hàm GUI, không hàm thứ ba. Xác nhận là bước (3) của §3.7 — chỉ tại đây số dư mới đổi.
 * `choices` viết bằng CHỮ người đọc (review #811 M1: phiếu nội suy `{quyetDinh}` — không được hiện `xac_nhan`); 0023 map nhãn → approve/decline.
 */
export const PAY_CONFIRM_DECIDE = {
  id: "PAY-CONFIRM-DECIDE",
  uc: "@UC0199",
  tier: "T2",
  status: LIVE,
  roles: MANAGER_ROLES,
  flags: WRITE_FLAGS,
  slots: [
    /*
     * 🔴 US-AIV2-0112 SLOT-GROUP (@UC0300, follow-up H01 #1037): ô nhóm là ĐƯỜNG VÀO khi phiên chưa có nhóm — thủ quỹ ở ≥2 nhóm gõ "xác nhận
     * tiền Nam chuyển" trước đây bị `AI_GROUP_REQUIRED` loại trang khỏi menu. Vai vẫn do cổng kiểm ở nhóm ĐƯỢC CHỐT (`start()` + `confirm()`);
     * nhóm máy đoán từ câu (H01) chỉ là ứng viên đã qua cổng; phiếu IN nhóm (`{nhom}` + nhãn "Nhóm") để người soát trước khi số dư đổi.
     */
    SLOT_GROUP,
    {
      id: "nguoi",
      type: "member",
      required: true,
      ask: "Lời báo của ai?",
      clarify: "Có nhiều người tên giống vậy đang chờ đối chiếu — bạn nói lời báo của ai?",
      // 🔴 re-review #1018 HIGH (@UC0320): 0 lời báo chờ (binding S08 nói tập rỗng) ⇒ câu ĐỌC này + đóng phiếu, không hỏi "Lời báo của ai?" trống.
      emptyByState: "Hiện chưa có ai báo đã chuyển tiền — không có lời báo nào đang chờ đối chiếu.",
    },
    // 🔴 re-review #1018 HIGH (@UC0320): quyết định chỉ có nghĩa SAU khi biết lời báo của ai ⇒ hỏi `nguoi` (kèm danh sách chờ) TRƯỚC; chữ LLM
    // gạch vào ô này từ một câu HỎI ("có ai báo đã chuyển tiền chưa") bị bỏ khi `nguoi` chưa rõ. Người dùng nói thẳng "xác nhận…" thì GIỮ.
    {
      id: "quyetDinh",
      type: "enum",
      required: true,
      ask: "Xác nhận đã nhận, hay từ chối?",
      choices: ["xác nhận", "từ chối"],
      askAfter: "nguoi",
      /*
       * 🔴 US-AIV2-0112 lô Sổ 2 (H03 mục 8) — "nhận được tiền của Quân rồi" ⇒ LLM gạch ô này = "nhận được" (3/3, probe `tuan` 29/09) ⇒ không khớp
       * lựa chọn nào ⇒ hỏi lại điều người dùng vừa nói. Cụm lấy từ ĐO (probe + span thật trong bản ghi eval: "duyệt", "không duyệt"). Không vào
       * prompt ⇒ không đổi bản ghi eval.
       * 🔴 review #1036 HIGH-1 (§3.7): "xác nhận" là lựa chọn KHẲNG ĐỊNH — số dư chỉ đổi ở bước này ⇒ `guardedChoices`: câu mang phủ định / nghi
       * vấn / ước đoán ("chưa nhận được", "nhận được chưa", "đã nhận được đâu", "hình như đã nhận", "?") KHÔNG BAO GIỜ ra "xác nhận" — chốt ở
       * `resolveEnumSlot` (mọi nhánh) và phép neo đưa CẢ CÂU cho resolver (`judgeSpan`: span ngắn "nhận được" không che "chưa" ngoài span).
       * "từ chối" không chốt (không làm tiền đi tiếp), trừ khi chính nó đứng sau từ phủ định ("đừng từ chối" ⇒ hỏi).
       */
      synonyms: { "xác nhận": ["nhận được", "đã nhận", "duyệt"], "từ chối": ["không duyệt"] },
      guardedChoices: ["xác nhận"],
      /*
       * 🔴 US-AIV2-0112 H08(a) (@UC0199 · QĐ PO 30/09 P9 "cho hiểu thẳng"): "tiền Quân chuyển về rồi" / "anh nhận được tiền của Quân rồi" ⇒ ô này =
       * "xác nhận" (vẫn phải bấm Đồng ý). Model KHÔNG gạch ô này cho câu kiểu đó (lô Sổ 3: 3/3), nudge thì model viết chữ ngoài câu ⇒ chữ đến từ
       * CODE: khuôn ĐÓNG `parseMoneyArrived` + tên đúng chỗ = ô `nguoi` Phiếu đã gạch (`fillReceivedDecisionFromSentence`). Chỉ ra "xác nhận",
       * không bao giờ "từ chối"; phủ định/nghi vấn ⇒ hỏi (chốt `guardedChoices` ở trên). KHÔNG vào prompt ⇒ không đổi bản ghi eval.
       */
      fromSentence: "money-received",
    },
    { id: "lyDo", type: "text", required: false, ask: "Lý do (chỉ khi từ chối, người báo sẽ đọc được)?", textForm: "note" },
  ],
  llm: {
    description: "Người quản lý xác nhận đã nhận tiền, hoặc từ chối, một lời báo chuyển khoản của thành viên.",
    examples: [
      "xác nhận đã nhận tiền của Minh",
      "xac nhan da nhan tien cua minh",
      "duyệt lời báo chuyển khoản của chị Lan",
      "duyet loi bao cua chi lan",
      "từ chối lời báo của Hùng, chưa thấy tiền",
      "tu choi loi bao cua hung",
      "xác nhận đã nhận",
      "ok tiền Minh đã vào",
      "chưa nhận được tiền của Lan, từ chối",
      "duyệt khai báo",
      "tiền Hùng vào rồi",
      "từ chối",
      // ——— 0105: thêm cho đủ 40 ———
      "đã nhận tiền của An, xác nhận giúp",
      "da nhan tien cua an xac nhan giup",
      "tiền vào tài khoản rồi, duyệt lời báo",
      "tien vao tai khoan roi duyet loi bao",
      "xác nhận khoản chuyển khoản của Bình",
      "xac nhan khoan chuyen khoan cua binh",
      "kiểm tra ngân hàng không thấy tiền, từ chối lời báo",
      "kiem tra ngan hang khong thay tien tu choi loi bao",
      "báo chuyển của bạn này sai số tiền, từ chối",
      "bao chuyen cua ban nay sai so tien tu choi",
      "duyệt lời báo đang chờ",
      "duyet loi bao dang cho",
      "đối chiếu xong rồi, xác nhận nhé",
      "doi chieu xong roi xac nhan nhe",
      "từ chối khai báo đã chuyển của Lan",
      "tu choi khai bao da chuyen cua lan",
      "ok đã nhận đủ tiền của An",
      "ok da nhan du tien cua an",
      "không tìm thấy giao dịch, không duyệt",
      "khong tim thay giao dich khong duyet",
      "chấp nhận lời báo chuyển khoản",
      "chap nhan loi bao chuyen khoan",
      "bác lời báo này",
      "bac loi bao nay",
      "xác nhận tiền đã nhận cho đợt thu tháng này",
      "xac nhan tien da nhan cho dot thu thang nay",
      "duyệt hay từ chối lời báo thì làm ở đâu?",
      "duyet hay tu choi loi bao thi lam o dau",
    ],
    // AI-INTERP M12 (QĐ I12): câu THỬ — KHÔNG vào prompt/BM25; viết đời thường, không chép ví dụ (validator HELD_OUT_IS_EXAMPLE).
    heldOutExamples: [
      "tien chi Lan vo roi, xac nhan dum",
      "check bank thấy tiền anh Hùng rồi, duyệt nha",
      "ko thay tien cua Minh dau, tu choi di",
      "em kiểm tra rồi, tiền của mẹ bé Na vào đủ ạ",
      "Lan bao ck ma chua thay dong nao, bac di",
      "ok tien An ve tk roi nhe",
    ],
    // 🔴 US-AIV2-0111 C06 (@UC0320) — câu HỎI (việc XEM) không thuộc trang ghi này; mỗi câu là ví dụ của một trang đọc ⇒ có ca eval.
    // review #1018 HIGH-1: KHÔNG khai "có ai báo đã chuyển tiền chưa" — trang này mở ra đúng danh sách lời báo đang chờ (0 thẻ ghi tới
    // khi người chọn người + quyết định), nên với câu đó nó là đáp án CHẤP NHẬN (AC C06(a)), không phải trang sai.
    questionsNotHere: ["còn ai chưa chuyển tiền không", "con ai chua chuyen tien khong"],
  },
  executor: "approveDecline.decide",
  /**
   * 🔴 US-AIV2-0112 PCD-MULTI-CLAIM-TEXT (@UC0199) — màn DUYỆT một lời báo (`payment-claims/:memberDueId`, cùng màn tin "có khoản nộp chờ bạn duyệt"
   * mở). Lượt lỗi ≥2 lời báo của một người (`AI_PAY_CONFIRM_AMBIGUOUS_PENDING`) ⇒ một nút mở màn này cho TỪNG lời báo (`:memberDueId` từ tham số
   * lỗi — bảng đóng `FAILURE_CLAIM_SCREENS` ở `ai-doer.service.ts`): chat không chọn hộ (§4), người chọn trên màn có đủ chi tiết. Không vào
   * prompt/chỉ mục tìm trang ⇒ 0 ca eval đổi.
   */
  guiScreen: "/groups/:groupId/payment-claims/:memberDueId",
  screenShows: "payment_claim_review",
  /** `@UC0216` — cửa mobile trang này chịu trách nhiệm: đối chiếu từng lời khai + batch 'tất cả' + danh sách lời khai đang chờ. */
  endpoints: [
    "POST /payment-confirmations/{confirmationId}/approve",
    "POST /payment-confirmations/{confirmationId}/decline",
    "POST /member-dues/confirmations/batch",
    "GET /member-dues/{memberDueId}/confirmations",
    // US-E6.F3-0913 (@UC0051 @UC9588): màn lời khai đọc THEO ID — lời khai "Quỹ chung" (không có khoản kỳ thu) mở từ dòng chờ duyệt + tin.
    "GET /payment-confirmations/{confirmationId}",
  ],
  // H08(a) (@UC0199): `viec` = câu của QUYẾT ĐỊNH do binding đọc từ ô `quyetDinh` ("xác nhận đã nhận" · "từ chối lời báo") — thẻ nói rõ ĐÃ NHẬN bao nhiêu.
  facts: ["tenNguoi", "soTien", "tenDot", "viec"],
  templates: {
    // 0110 S13 (@UC0255): mở bằng chữ cố định (xem pay-on-behalf.ts); US-AIV2-0112 SLOT-GROUP (@UC0300): chữ cố định nay là nhóm —
    // *"Ở nhóm CLB cầu lông, bạn xác nhận…?"* (nhóm máy đoán phải hiện trước Đồng ý).
    // 🔴 US-AIV2-0112 H08(a) (@UC0199, §3.7): bước (3) là XÁC NHẬN ĐÃ NHẬN TIỀN ⇒ thẻ nói đúng việc đó + SỐ CỦA LỜI BÁO (binding, không từ câu):
    // *"… bạn xác nhận đã nhận 300,000 đ của Minh cho “Quỹ tháng 9”?"* · *"… bạn từ chối lời báo 300,000 đ của Minh cho …?"*.
    confirm: "Ở nhóm {nhom}, bạn {viec} {soTien} của {tenNguoi} cho “{tenDot}”?",
    done: "Đã {quyetDinh} lời báo của {tenNguoi} ({soTien}, “{tenDot}”). Người báo sẽ nhận thông báo.",
  },
  /**
   * 🔴 Nhãn = chữ NGƯỜI ĐỌC trên thẻ xác nhận (review #811 M1 — phiếu nội suy `{quyetDinh}`, không được
   * hiện `xac_nhan`). Thiếu nhãn ⇒ `gen-acceptance-checklist` báo **MÙ (rc=2)** và thẻ ném lúc chạy.
   */
  labels: {
    nhom: "Nhóm",
    quyetDinh: "Quyết định",
    viec: "Quyết định",
    tenNguoi: "Người báo",
    soTien: "Số tiền",
    tenDot: "Đợt thu",
  },
  // 0110 S13 (@UC0255): bỏ RECORD-PAID — sau khi XÁC NHẬN, khoản đã vào sổ; gợi "Ghi nhận đã đóng" là mời ghi TRÙNG (ảnh máy ảo 25/09).
  journey: {
    outcomes: {
      done: { next: ["READ-COLLECTIONS"] },
      // US-AIV2-tourguide-0005 (@UC0364): 0 lời báo chờ (ô `nguoi` emptyByState) ⇒ phiếu đóng với câu đọc; mời xem đợt thu (cùng nút như cũ).
      empty: { next: ["READ-COLLECTIONS"] },
    },
  },
  help: { title: "Duyệt báo đã nộp tiền", tryNow: "xác nhận đã nhận tiền của Minh" },
} satisfies AiCapabilityPage;
